import type { Context, Dispatch, ReactNode } from 'react';
import type { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';

// #region Setup
export interface SavedSessionCookieData {
    appointmentID?: number;
    appointmentKey: string;
    route: string;
}

export interface SavedSessionContextState extends SavedSessionCookieData {
    showSaveButton?: boolean;
}

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type SavedSessionDispatchState = Dispatch<SavedSessionActions>;

/**
 * Defines the type for the state context.
 */
export type SavedSessionState = SavedSessionContextState | undefined;

/**
 * Defines the type for the dispatch context.
 */
export type SavedSessionDispatch = SavedSessionDispatchState | undefined;

/**
 * The SavedSession state context type that is returned after the creation of the state context.
 */
export type SavedSessionStateContextType = Context<SavedSessionState>;

/**
 * The SavedSession dispatch context type that is returned after the creation of the dispatch context.
 */
export type SavedSessionDispatchContextType = Context<SavedSessionDispatch>;
// #endregion

// #region Reducer
export enum SavedSessionDispatchTypes {
    Update = 'SAVED_SESSION_UPDATE',
    ShowSaveButton = 'SHOW_SAVE_BUTTON'
}

export interface SavedSessionPayload {
    [SavedSessionDispatchTypes.Update]: SavedSessionCookieData;
    [SavedSessionDispatchTypes.ShowSaveButton]: boolean;
}

export type SavedSessionActions = ActionMap<SavedSessionPayload>[keyof ActionMap<SavedSessionPayload>];

/**
 * Defines the type for the SavedSessionReducer function.
 */
export type SavedSessionReducerFunction = GoMotoFunction<
    [SavedSessionContextState, SavedSessionActions],
    SavedSessionContextState
>;
// #endregion

// #region Provider
export interface SavedSessionProviderProps {
    children: ReactNode;
}
// #endregion

// #region Hooks
/**
 * The function type for the `useSavedSessionState` hook. Takes no arguments and returns the current SavedSession state.
 */
export type SavedSessionStateHook = GoMotoFunction<
    [void],
    SavedSessionContextState
>;

/**
 * The function type for the `useSavedSessionDispatch` hook. Takes no arguments and returns a dispatch function for the SavedSession context.
 */
export type SavedSessionDispatchHook = GoMotoFunction<
    [void],
    SavedSessionDispatchState
>;
// #endregion